var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, useMemo } from 'react';
import classNames from 'classnames';
export var AvatarIndicatorColor;
(function (AvatarIndicatorColor) {
    AvatarIndicatorColor["error"] = "error";
    AvatarIndicatorColor["success"] = "success";
    AvatarIndicatorColor["gray"] = "gray";
    AvatarIndicatorColor["warning"] = "warning";
})(AvatarIndicatorColor || (AvatarIndicatorColor = {}));
function Indicator(_a) {
    var size = _a.size, color = _a.color, children = _a.children;
    var sizeClass = useMemo(function () {
        switch (size) {
            case "xs":
                return "h-1.5 w-1.5";
            case "sm":
                return "h-2 w-2";
            case "md":
            default:
                return "h-2.5 w-2.5";
            case "lg":
                return "h-3 w-3";
            case "xl":
                return "h-3.5 w-3.5";
            case "2xl":
                return "h-4 w-4";
        }
    }, [size]);
    var colorClass = useMemo(function () {
        switch (color) {
            case AvatarIndicatorColor.error:
                return "bg-error-500";
            case AvatarIndicatorColor.success:
                return "bg-success-500";
            case AvatarIndicatorColor.gray:
                return "bg-gray-300";
            case AvatarIndicatorColor.warning:
                return "bg-warning-500";
            default:
                return "bg-gray-100";
        }
    }, [color]);
    return React.createElement("span", { className: "absolute bottom-0 right-0 block translate-x-1/2 translate-y-1/2 transform rounded-full border-2 border-white items-center justify-center flex" },
        React.createElement("span", { className: classNames("absolute bottom-0 right-0 block rounded-full ring-2 ring-white items-center justify-center flex", sizeClass, colorClass) }, children));
}
var hoverEvents = 'ring-8 ring-gray-200/0 hover:ring-gray-200 focus:ring-gray-200 transition-ring duration-200 ease-in-out active:bg-gray-100';
export var Avatar = forwardRef(function AvatarComponent(_a, ref) {
    var _b;
    var _c = _a.as, Tag = _c === void 0 ? 'div' : _c, name = _a.name, src = _a.src, children = _a.children, _d = _a.size, size = _d === void 0 ? "md" : _d, indicator = _a.indicator, interactable = _a.interactable, className = _a.className, props = __rest(_a, ["as", "name", "src", "children", "size", "indicator", "interactable", "className"]);
    var innerContent = React.useMemo(function () {
        if (children) {
            return children;
        }
        if (src) {
            return React.createElement("img", { alt: Array.isArray(name) ? name.join(' ') : name, className: 'h-full w-full object-center object-cover', src: src });
        }
        if (name) {
            var initials = void 0;
            if (Array.isArray(name)) {
                initials = name;
            }
            else {
                initials = name.toUpperCase().split(/\s/);
            }
            initials = initials
                .map(function (n) { return n && n[0]; })
                .filter(function (n) { return n; });
            switch (initials.length) {
                case 0:
                    break;
                case 1:
                    return React.createElement("span", null, initials[0]);
                default:
                    return React.createElement("span", null,
                        initials[0],
                        initials[initials.length - 1]);
            }
        }
        return React.createElement("div", null);
    }, [children, name, src]);
    var sizeClass = useMemo(function () {
        switch (size) {
            case "xs":
                return "text-xs h-6 w-6";
            case "sm":
                return "text-sm h-8 w-8";
            case "md":
            default:
                return "text-md h-10 w-10";
            case "lg":
                return "text-lg h-12 w-12";
            case "xl":
                return "text-xl h-14 w-14";
            case "2xl":
                return "text-2xl h-16 w-16";
        }
    }, [size]);
    return (React.createElement(Tag, __assign({ ref: ref, className: classNames("relative font-semibold text-gray-600 inline-flex items-center justify-center select-none rounded-full", sizeClass, className, (_b = {}, _b[hoverEvents] = interactable, _b)) }, props),
        React.createElement("div", { className: 'w-full h-full flex items-center justify-center rounded-full bg-gray-100 overflow-hidden' }, innerContent),
        indicator && (Object.values(AvatarIndicatorColor).includes(indicator)
            ? React.createElement(Indicator, { color: indicator, size: size })
            : React.createElement(Indicator, { size: size }, indicator))));
});
